import { computed } from 'vue'
import { useSessionStore } from '@/stores/session'

/**
 * Permission constants matching backend Permission.php
 */
export const PERMISSIONS = {
    // Audit
    AUDIT_LOG_READ: 'audit_log.read',

    // Account
    ACCOUNT_READ: 'account.read',
    ACCOUNT_UPDATE: 'account.update',
    ACCOUNT_DELETE: 'account.delete',

    // Billing
    BILLING_READ: 'billing.read',
    BILLING_UPDATE: 'billing.update',

    // User management
    USER_LIST: 'user.list',
    USER_CREATE: 'user.create',
    USER_READ: 'user.read',
    USER_UPDATE: 'user.update',
    USER_DELETE: 'user.delete',

    // Team management
    TEAM_LIST: 'team.list',
    TEAM_CREATE: 'team.create',
    TEAM_READ: 'team.read',
    TEAM_UPDATE: 'team.update',
    TEAM_DELETE: 'team.delete',

    // Projects
    PROJECT_LIST: 'project.list',
    PROJECT_CREATE: 'project.create',
    PROJECT_READ: 'project.read',
    PROJECT_UPDATE: 'project.update',
    PROJECT_DELETE: 'project.delete',

    // Environments
    ENVIRONMENT_LIST: 'environment.list',
    ENVIRONMENT_CREATE: 'environment.create',
    ENVIRONMENT_READ: 'environment.read',
    ENVIRONMENT_UPDATE: 'environment.update',
    ENVIRONMENT_DELETE: 'environment.delete',

    // Flags
    FLAG_LIST: 'flag.list',
    FLAG_CREATE: 'flag.create',
    FLAG_READ: 'flag.read',
    FLAG_UPDATE: 'flag.update',
    FLAG_DELETE: 'flag.delete',

    // Targets
    TARGET_LIST: 'target.list',
    TARGET_CREATE: 'target.create',
    TARGET_READ: 'target.read',
    TARGET_UPDATE: 'target.update',
    TARGET_DELETE: 'target.delete',

    // Segments
    SEGMENT_LIST: 'segment.list',
    SEGMENT_CREATE: 'segment.create',
    SEGMENT_READ: 'segment.read',
    SEGMENT_UPDATE: 'segment.update',
    SEGMENT_DELETE: 'segment.delete',

    // Webhooks
    WEBHOOK_LIST: 'webhook.list',
    WEBHOOK_CREATE: 'webhook.create',
    WEBHOOK_READ: 'webhook.read',
    WEBHOOK_UPDATE: 'webhook.update',
    WEBHOOK_DELETE: 'webhook.delete',

    // Contexts
    CONTEXT_LIST: 'context.list',
    CONTEXT_READ: 'context.read',
}

/**
 * Role-to-permission mapping matching backend Permission::ROLE_PERMISSIONS.
 * A Map (rather than a plain object keyed by role) so looking it up by a
 * variable role name reads as `.get(role)` instead of bracket-notation
 * property access.
 */
const ROLE_PERMISSIONS = new Map(
    Object.entries({
        owner: '*',
        admin: '*',
        editor: [
            PERMISSIONS.ACCOUNT_READ,
            PERMISSIONS.AUDIT_LOG_READ,
            PERMISSIONS.PROJECT_LIST,
            PERMISSIONS.PROJECT_READ,
            PERMISSIONS.ENVIRONMENT_LIST,
            PERMISSIONS.ENVIRONMENT_CREATE,
            PERMISSIONS.ENVIRONMENT_READ,
            PERMISSIONS.ENVIRONMENT_UPDATE,
            PERMISSIONS.ENVIRONMENT_DELETE,
            PERMISSIONS.FLAG_LIST,
            PERMISSIONS.FLAG_CREATE,
            PERMISSIONS.FLAG_READ,
            PERMISSIONS.FLAG_UPDATE,
            PERMISSIONS.FLAG_DELETE,
            PERMISSIONS.TARGET_LIST,
            PERMISSIONS.TARGET_CREATE,
            PERMISSIONS.TARGET_READ,
            PERMISSIONS.TARGET_UPDATE,
            PERMISSIONS.TARGET_DELETE,
            PERMISSIONS.SEGMENT_LIST,
            PERMISSIONS.SEGMENT_CREATE,
            PERMISSIONS.SEGMENT_READ,
            PERMISSIONS.SEGMENT_UPDATE,
            PERMISSIONS.SEGMENT_DELETE,
            PERMISSIONS.WEBHOOK_LIST,
            PERMISSIONS.WEBHOOK_CREATE,
            PERMISSIONS.WEBHOOK_READ,
            PERMISSIONS.WEBHOOK_UPDATE,
            PERMISSIONS.WEBHOOK_DELETE,
            PERMISSIONS.CONTEXT_LIST,
            PERMISSIONS.CONTEXT_READ,
        ],
        viewer: [
            PERMISSIONS.ACCOUNT_READ,
            PERMISSIONS.AUDIT_LOG_READ,
            PERMISSIONS.PROJECT_LIST,
            PERMISSIONS.PROJECT_READ,
            PERMISSIONS.ENVIRONMENT_LIST,
            PERMISSIONS.ENVIRONMENT_READ,
            PERMISSIONS.FLAG_LIST,
            PERMISSIONS.FLAG_READ,
            PERMISSIONS.TARGET_LIST,
            PERMISSIONS.TARGET_READ,
            PERMISSIONS.SEGMENT_LIST,
            PERMISSIONS.SEGMENT_READ,
            PERMISSIONS.CONTEXT_LIST,
            PERMISSIONS.CONTEXT_READ,
            PERMISSIONS.WEBHOOK_LIST,
            PERMISSIONS.WEBHOOK_READ,
        ],
    })
)

/**
 * Whether a given role grants a specific permission, matching backend
 * AuthorizationService::grantAllowsPermission.
 */
function roleAllows(role, permission) {
    if (!role) return false

    const perms = ROLE_PERMISSIONS.get(role)
    if (perms === '*') {
        // Admin has wildcard access except account deletion, which is owner-only.
        if (role === 'admin' && permission === PERMISSIONS.ACCOUNT_DELETE) {
            return false
        }
        return true
    }

    return Array.isArray(perms) && perms.includes(permission)
}

/**
 * Composable for checking user permissions based on their role.
 *
 * A user's account-wide role (`effective_role`) is checked first. When that
 * doesn't grant the permission, `can()` falls back to any project- or
 * environment-scoped grant the user holds (`grant_scopes`), so users whose
 * only access is scoped to a specific project/environment (direct or via a
 * team) still see the actions they're actually allowed to take.
 *
 * Usage:
 *   const { can, canAny, canAll, role, isAdmin, isOwner } = usePermissions()
 *   if (can(PERMISSIONS.FLAG_CREATE)) { ... }
 *   if (can(PERMISSIONS.FLAG_UPDATE, { projectUlid, environmentUlid })) { ... }
 */
export function usePermissions() {
    const sessionStore = useSessionStore()

    const role = computed(
        () => sessionStore.data?.user?.data?.effective_role ?? null
    )

    // Roles are converted to Maps (instead of read via bracket-notation
    // property access on the raw project_roles/environment_roles objects)
    // purely to keep the lookups below as `.get(ulid)` calls.
    const grantScopes = computed(() => {
        const scopes = sessionStore.data?.user?.data?.grant_scopes
        return {
            projectRoles: new Map(Object.entries(scopes?.project_roles ?? {})),
            environmentRoles: new Map(
                Object.entries(scopes?.environment_roles ?? {})
            ),
        }
    })

    const isOwner = computed(() => role.value === 'owner')
    const isAdmin = computed(
        () => role.value === 'admin' || role.value === 'owner'
    )
    const isEditor = computed(() => role.value === 'editor')
    const isViewer = computed(() => role.value === 'viewer')

    /**
     * Check if current user has a specific permission, optionally scoped to
     * a project and/or environment.
     */
    function can(permission, { projectUlid, environmentUlid } = {}) {
        if (roleAllows(role.value, permission)) return true

        const scopes = grantScopes.value
        if (
            environmentUlid &&
            roleAllows(scopes.environmentRoles.get(environmentUlid), permission)
        ) {
            return true
        }

        if (
            projectUlid &&
            roleAllows(scopes.projectRoles.get(projectUlid), permission)
        ) {
            return true
        }

        return false
    }

    /**
     * Check if current user has any of the given permissions.
     */
    function canAny(permissions, scope = {}) {
        return permissions.some((p) => can(p, scope))
    }

    /**
     * Check if current user has all of the given permissions.
     */
    function canAll(permissions, scope = {}) {
        return permissions.every((p) => can(p, scope))
    }

    return {
        role,
        isOwner,
        isAdmin,
        isEditor,
        isViewer,
        can,
        canAny,
        canAll,
    }
}
